/**
 * Flow temperature / ΔT correction for radiator & towel rail heat outputs.
 *
 * State: persisted in localStorage under FLOW_STORAGE_KEY (see setSelection).
 * Recalculation: T50 metafield bases × current correction factor; display-only (never writes metafields).
 * DOM updates: refreshAll() walks [data-rfhp-flow-heat]; MutationObserver + capture-phase variant:update
 * catch AJAX section morphs and product-card variant changes (variant:update bubbles but may be stopped on bubble).
 * First-visit coach: [data-rfhp-flow-temp-coach]; dismissed via COACH_STORAGE_KEY or opening the flow menu.
 * Coach uses position:fixed + getBoundingClientRect so it is not covered by main content stacking.
 * Mobile (≤749px): fixed panel under header, dimmed backdrop, scroll lock, 44px+ touch targets.
 */

const FLOW_STORAGE_KEY = 'flow_temperature_selection';
const COACH_STORAGE_KEY = 'rfhp_flow_temp_coach_dismissed';
const FLOW_EVENT = 'flow-temperature-changed';
const VARIANT_UPDATE = 'variant:update';

/** @typedef {{ id: string, label: string, tempC: number, delta: string, factor: number }} FlowOption */

/** @type {FlowOption[]} */
const FLOW_OPTIONS = [
  { id: '30', label: '30°C (ΔT10)', tempC: 30, delta: 'ΔT10', factor: 0.12 },
  { id: '35', label: '35°C (ΔT15)', tempC: 35, delta: 'ΔT15', factor: 0.21 },
  { id: '40', label: '40°C (ΔT20)', tempC: 40, delta: 'ΔT20', factor: 0.3 },
  { id: '45', label: '45°C (ΔT25)', tempC: 45, delta: 'ΔT25', factor: 0.41 },
  { id: '50', label: '50°C (ΔT30) - Most heat pump systems', tempC: 50, delta: 'ΔT30', factor: 0.51 },
  { id: '55', label: '55°C (ΔT35)', tempC: 55, delta: 'ΔT35', factor: 0.63 },
  { id: '60', label: '60°C (ΔT40)', tempC: 60, delta: 'ΔT40', factor: 0.75 },
  { id: '65', label: '65°C (ΔT45)', tempC: 65, delta: 'ΔT45', factor: 0.87 },
  { id: '70', label: '70°C (ΔT50) - Most boiler systems', tempC: 70, delta: 'ΔT50', factor: 1.0 },
];

const DEFAULT_OPTION = FLOW_OPTIONS.find((o) => o.id === '50') || FLOW_OPTIONS[4];

/** @param {unknown} n */
function numOrNull(n) {
  if (n == null || n === '') return null;
  const x = Number(n);
  return Number.isFinite(x) ? x : null;
}

/** @param {number | null} v */
function roundDisplay(v) {
  if (v == null || !Number.isFinite(v)) return null;
  return Math.round(v);
}

/**
 * @param {string | null | undefined} jsonText
 * @returns {Record<string, { w: number | null; b: number | null }>}
 */
function parseVariantHeatMap(jsonText) {
  if (!jsonText) return {};
  try {
    const arr = JSON.parse(jsonText);
    if (!Array.isArray(arr)) return {};
    /** @type {Record<string, { w: number | null; b: number | null }>} */
    const out = {};
    for (const row of arr) {
      if (!row || row.id == null) continue;
      const id = String(row.id);
      out[id] = { w: numOrNull(row.w), b: numOrNull(row.b) };
    }
    return out;
  } catch {
    return {};
  }
}

/** @param {HTMLElement} card */
function getActiveVariantIdForCard(card) {
  const checked = card.querySelector('input[type="radio"]:checked[data-variant-id]');
  if (checked instanceof HTMLInputElement && checked.dataset.variantId) return checked.dataset.variantId;
  const dv = card.querySelector('[data-rfhp-default-variant-id]');
  if (dv instanceof HTMLElement && dv.dataset.rfhpDefaultVariantId) return dv.dataset.rfhpDefaultVariantId;
  return null;
}

const FlowTemperatureManager = {
  /** @type {FlowOption | null} */
  _current: null,
  _initialized: false,
  /** @type {((e: Event) => void) | null} */
  _variantCaptureListener: null,
  /** @type {((e: Event) => void) | null} */
  _outsidePointerListener: null,
  _mo: /** @type {MutationObserver | null} */ (null),
  _debounceTimer: /** @type {ReturnType<typeof setTimeout> | null} */ (null),
  _coachTimer: /** @type {ReturnType<typeof setTimeout> | null} */ (null),
  /** @type {Array<[HTMLElement, () => void]> | null} */
  _coachDismissBtnHandlers: null,
  /** @type {(() => void) | null} */
  _coachRepositionListener: null,
  _mobilePanelScrollLocked: false,

  getOptions() {
    return FLOW_OPTIONS.slice();
  },

  getCorrectionFactor() {
    return this._current?.factor ?? DEFAULT_OPTION.factor;
  },

  getSelection() {
    return this._current ? { ...this._current } : { ...DEFAULT_OPTION };
  },

  /** @param {FlowOption} opt */
  _persist(opt) {
    try {
      const payload = {
        selectedTemperature: String(opt.tempC),
        deltaLabel: opt.delta,
        correctionFactor: opt.factor,
        optionId: opt.id,
      };
      localStorage.setItem(FLOW_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* private mode / quota */
    }
  },

  /** @returns {FlowOption} */
  _loadFromStorage() {
    try {
      const raw = localStorage.getItem(FLOW_STORAGE_KEY);
      if (!raw) return DEFAULT_OPTION;
      const o = JSON.parse(raw);
      const id = o.optionId != null ? String(o.optionId) : null;
      if (id) {
        const found = FLOW_OPTIONS.find((x) => x.id === id);
        if (found) return found;
      }
      const tc = numOrNull(o.selectedTemperature);
      if (tc != null) {
        const match = FLOW_OPTIONS.find((x) => x.tempC === tc);
        if (match) return match;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_OPTION;
  },

  /** @param {number | null} baseW @param {number | null} baseB */
  computeOutputs(baseW, baseB) {
    const f = this.getCorrectionFactor();
    return {
      watts: baseW != null ? baseW * f : null,
      btus: baseB != null ? baseB * f : null,
    };
  },

  /** @param {number | null} v */
  formatWatts(v) {
    const r = roundDisplay(v);
    return r == null ? '' : `${r}W`;
  },

  /** @param {number | null} v */
  formatBtus(v) {
    const r = roundDisplay(v);
    return r == null ? '' : `${r} BTUs`;
  },

  _emitChange() {
    const detail = { selection: this.getSelection() };
    document.dispatchEvent(new CustomEvent(FLOW_EVENT, { bubbles: true, detail }));
  },

  _allFlowSelects() {
    return document.querySelectorAll('details[data-rfhp-flow-temp] select.rfhp-flow-temp__select');
  },

  _bindSelect() {
    const selects = this._allFlowSelects();
    if (!selects.length) return;

    selects.forEach((sel) => {
      sel.addEventListener('change', () => {
        if (!(sel instanceof HTMLSelectElement)) return;
        const id = sel.value;
        const opt = FLOW_OPTIONS.find((x) => x.id === id) || DEFAULT_OPTION;
        this._current = opt;
        this._persist(opt);
        this._syncSelectDom();
        this.refreshAll();
        this._emitChange();
        this._closeFlowPanels();
      });
    });
  },

  /** Collapse every header ΔT popover (desktop + mobile instances). */
  _closeFlowPanels() {
    document.querySelectorAll('details[data-rfhp-flow-temp]').forEach((d) => {
      if (d instanceof HTMLDetailsElement) {
        d.open = false;
        this._syncFlowDetailsMobile(d);
      }
    });
  },

  _isMobileFlowViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  },

  _lockMobilePanelScroll() {
    if (this._mobilePanelScrollLocked) return;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    this._mobilePanelScrollLocked = true;
  },

  _unlockMobilePanelScroll() {
    if (!this._mobilePanelScrollLocked) return;
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    this._mobilePanelScrollLocked = false;
  },

  /**
   * @param {HTMLElement} panel
   */
  _positionMobileFlowPanel(panel) {
    const header =
      document.getElementById('header-component') || document.querySelector('#header-group header');
    if (!(header instanceof HTMLElement)) return;
    const top = Math.ceil(header.getBoundingClientRect().bottom) + 8;
    panel.style.setProperty('top', `${top}px`);
  },

  /**
   * @param {HTMLElement} panel
   */
  _clearMobileFlowPanelStyles(panel) {
    panel.style.removeProperty('top');
  },

  /**
   * @param {HTMLDetailsElement} det
   */
  _syncFlowDetailsMobile(det) {
    const backdrop = det.querySelector('[data-rfhp-flow-temp-backdrop]');
    const panel = det.querySelector('.rfhp-flow-temp__panel');
    const isMobile = this._isMobileFlowViewport();

    if (backdrop instanceof HTMLElement) {
      if (det.open && isMobile) backdrop.removeAttribute('hidden');
      else backdrop.setAttribute('hidden', '');
    }

    if (det.open && isMobile) {
      if (panel instanceof HTMLElement) this._positionMobileFlowPanel(panel);
      this._lockMobilePanelScroll();
    } else {
      if (panel instanceof HTMLElement) this._clearMobileFlowPanelStyles(panel);
      if (!document.querySelector('details[data-rfhp-flow-temp][open]')) {
        this._unlockMobilePanelScroll();
      }
    }
  },

  _bindFlowPanelMobile() {
    const onViewportChange = () => {
      document.querySelectorAll('details[data-rfhp-flow-temp][open]').forEach((det) => {
        if (!(det instanceof HTMLDetailsElement)) return;
        this._syncFlowDetailsMobile(det);
      });
    };
    window.addEventListener('resize', onViewportChange);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onViewportChange);
    }

    document.querySelectorAll('details[data-rfhp-flow-temp]').forEach((det) => {
      if (!(det instanceof HTMLDetailsElement)) return;

      det.addEventListener('toggle', () => {
        this._syncFlowDetailsMobile(det);
        if (det.open) this._dismissFlowCoach();
      });

      const backdrop = det.querySelector('[data-rfhp-flow-temp-backdrop]');
      if (backdrop instanceof HTMLElement) {
        backdrop.addEventListener('click', (e) => {
          e.preventDefault();
          det.open = false;
        });
      }
    });
  },

  _syncSelectDom() {
    const opt = this._current || DEFAULT_OPTION;
    const short = `${opt.tempC}°C (${opt.delta})`;
    const title = `Flow Temperature / Delta T: ${short}`;

    this._allFlowSelects().forEach((sel) => {
      if (sel instanceof HTMLSelectElement) sel.value = opt.id;
    });

    document.querySelectorAll('details[data-rfhp-flow-temp]').forEach((det) => {
      if (!(det instanceof HTMLDetailsElement)) return;
      det.querySelectorAll('[data-rfhp-flow-delta-glyph]').forEach((el) => {
        el.textContent = opt.delta;
      });
      det.querySelectorAll('.rfhp-flow-temp__current').forEach((el) => {
        el.textContent = short;
      });
      det.querySelectorAll('.rfhp-flow-temp__summary').forEach((el) => {
        el.textContent = title;
      });
      const trig = det.querySelector('.rfhp-flow-temp__trigger');
      if (trig instanceof HTMLElement) {
        trig.title = title;
        trig.setAttribute('aria-label', `Flow temperature, ${opt.delta}. Open to change.`);
      }
    });
  },

  /** @param {HTMLElement} root */
  _applySizeCard(root) {
    const factor = this.getCorrectionFactor();
    const delta = this._current?.delta ?? DEFAULT_OPTION.delta;

    root.querySelectorAll('[data-size-card][data-rfhp-watts-base]').forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      const wBase = numOrNull(btn.dataset.rfhpWattsBase);
      const bBase = numOrNull(btn.dataset.rfhpBtuBase);
      const wEl = btn.querySelector('[data-rfhp-heat-watts]');
      const bEl = btn.querySelector('[data-rfhp-heat-btu]');
      const wDt = btn.querySelector('[data-rfhp-dt-watts]');
      const bDt = btn.querySelector('[data-rfhp-dt-btu]');

      if (wBase != null && wEl) {
        wEl.textContent = this.formatWatts(wBase * factor);
      }
      if (bBase != null && bEl) {
        bEl.textContent = this.formatBtus(bBase * factor);
      }
      if (wDt) wDt.textContent = delta;
      if (bDt) bDt.textContent = delta;
    });
  },

  /** @param {HTMLElement} root */
  _applyConfiguratorSummary(root) {
    const cfg = root.closest('product-configurator');
    if (!cfg) return;
    const line = cfg.querySelector('[data-summary-line]');
    if (!(line instanceof HTMLElement)) return;
    const heatBlock = line.querySelector('[data-rfhp-summary-heat-block]');
    const heatSpan = line.querySelector('[data-rfhp-summary-heat]');
    if (!(heatSpan instanceof HTMLElement)) return;

    const vid = cfg.querySelector('input[name="id"]')?.value;
    const varEl = cfg.querySelector('[data-configurator-variants]');
    if (!vid || !varEl?.textContent) return;

    let variants;
    try {
      variants = JSON.parse(varEl.textContent);
    } catch {
      return;
    }
    const v = Array.isArray(variants) ? variants.find((x) => String(x.id) === String(vid)) : null;
    if (!v) return;

    const w50 = numOrNull(v.heat_watts_t50);
    const b50 = numOrNull(v.heat_btus_t50);
    if (w50 == null && b50 == null) {
      heatSpan.textContent = '';
      if (heatBlock instanceof HTMLElement) heatBlock.hidden = true;
      return;
    }

    if (heatBlock instanceof HTMLElement) heatBlock.hidden = false;

    const { watts, btus } = this.computeOutputs(w50, b50);
    const parts = [];
    if (watts != null) parts.push(this.formatWatts(watts));
    if (btus != null) parts.push(this.formatBtus(btus));
    heatSpan.textContent = parts.join(' · ');
  },

  /** @param {HTMLElement} wrap */
  _applyProductCardHeat(wrap) {
    const card = wrap.closest('product-card');
    if (!card) return;

    const script = wrap.querySelector('script[type="application/json"][data-rfhp-variant-heat-json]');
    const map = parseVariantHeatMap(script?.textContent ?? '');
    const vid = getActiveVariantIdForCard(card);
    if (!vid) return;

    const row = map[vid];
    if (!row) return;

    const { watts, btus } = this.computeOutputs(row.w, row.b);
    const line = wrap.querySelector('[data-rfhp-card-heat-text]');
    if (!(line instanceof HTMLElement)) return;

    const parts = [];
    if (watts != null) parts.push(this.formatWatts(watts));
    if (btus != null) parts.push(this.formatBtus(btus));
    line.textContent = parts.join(' · ');
    wrap.hidden = parts.length === 0;
  },

  refreshAll() {
    document.querySelectorAll('[data-rfhp-card-heat-wrap]').forEach((el) => {
      if (el instanceof HTMLElement) this._applyProductCardHeat(el);
    });
    document.querySelectorAll('[data-rfhp-flow-heat]').forEach((el) => {
      if (el instanceof HTMLElement) this._applySizeCard(el);
    });
    document.querySelectorAll('product-configurator').forEach((cfg) => {
      if (cfg instanceof HTMLElement) this._applyConfiguratorSummary(cfg);
    });
  },

  _scheduleRefresh() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this._debounceTimer = null;
      this.refreshAll();
    }, 48);
  },

  _onVariantUpdateCapture(e) {
    const card = e.target instanceof Element ? e.target.closest('product-card') : null;
    if (card) {
      const wrap = card.querySelector('[data-rfhp-card-heat-wrap]');
      if (wrap instanceof HTMLElement) this._applyProductCardHeat(wrap);
    }
    this._scheduleRefresh();
  },

  _observeDom() {
    if (this._mo) return;
    this._mo = new MutationObserver(() => this._scheduleRefresh());
    this._mo.observe(document.body, { childList: true, subtree: true });
  },

  /**
   * Outside-click dismiss: <details> does not close on backdrop taps by default.
   * Capture phase runs before inner handlers so we only react to true “outside” presses.
   */
  _onPointerDownOutside(e) {
    if (!(e.target instanceof Node)) return;
    document.querySelectorAll('details[data-rfhp-flow-temp][open]').forEach((det) => {
      if (!(det instanceof HTMLDetailsElement) || !det.open) return;
      if (!det.contains(e.target)) {
        det.open = false;
        this._syncFlowDetailsMobile(det);
      }
    });
  },

  _flowCoachDismissed() {
    if (typeof localStorage === 'undefined') return true;
    try {
      return localStorage.getItem(COACH_STORAGE_KEY) === '1';
    } catch {
      return true;
    }
  },

  _detachCoachReposition() {
    if (!this._coachRepositionListener) return;
    window.removeEventListener('scroll', this._coachRepositionListener, true);
    window.removeEventListener('resize', this._coachRepositionListener);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this._coachRepositionListener);
    }
    this._coachRepositionListener = null;
  },

  _attachCoachReposition() {
    if (this._coachRepositionListener) return;
    this._coachRepositionListener = () => {
      document.querySelectorAll('[data-rfhp-flow-temp-coach].rfhp-flow-temp__coach--visible').forEach((el) => {
        if (el instanceof HTMLElement) this._positionFlowCoach(el);
      });
    };
    window.addEventListener('scroll', this._coachRepositionListener, true);
    window.addEventListener('resize', this._coachRepositionListener);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this._coachRepositionListener);
    }
  },

  /**
   * @param {HTMLElement} coach
   */
  _positionFlowCoach(coach) {
    const wrap = coach.closest('[data-rfhp-flow-temp-wrap]');
    const trigger = wrap?.querySelector('summary.rfhp-flow-temp__trigger');
    const anchor = trigger instanceof HTMLElement ? trigger : wrap;
    if (!(anchor instanceof HTMLElement)) return;

    const r = anchor.getBoundingClientRect();
    const vw = window.visualViewport?.width ?? window.innerWidth;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const margin = 8;
    const gap = 10;
    const cw = coach.offsetWidth || 280;
    const ch = coach.offsetHeight || 120;

    let left = r.left + r.width / 2 - cw / 2;
    const maxLeft = vw - cw - margin;
    left = Math.max(margin, Math.min(left, maxLeft));

    let top = r.bottom + gap;
    const maxTop = vh - ch - margin;
    if (top > maxTop) top = Math.max(margin, maxTop);

    coach.style.setProperty('position', 'fixed');
    coach.style.setProperty('left', `${Math.round(left)}px`);
    coach.style.setProperty('top', `${Math.round(top)}px`);
    coach.style.setProperty('right', 'auto');
    coach.style.setProperty('bottom', 'auto');
    coach.style.setProperty('transform', 'none');
    /* Above header menus / drawers (--layer-menu-drawer: 18, --layer-temporary: 20) */
    coach.style.setProperty('z-index', '25');
  },

  _dismissFlowCoach() {
    if (this._coachTimer != null) {
      clearTimeout(this._coachTimer);
      this._coachTimer = null;
    }
    if (this._coachDismissBtnHandlers) {
      this._coachDismissBtnHandlers.forEach(([btn, fn]) => btn.removeEventListener('click', fn));
      this._coachDismissBtnHandlers = null;
    }
    this._detachCoachReposition();
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(COACH_STORAGE_KEY, '1');
    } catch {
      /* private mode */
    }
    document.querySelectorAll('[data-rfhp-flow-temp-coach]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.classList.remove('rfhp-flow-temp__coach--visible');
      ['position', 'left', 'top', 'right', 'bottom', 'transform', 'z-index'].forEach((prop) =>
        el.style.removeProperty(prop),
      );
      el.setAttribute('hidden', '');
    });
  },

  _initFlowCoach() {
    if (this._flowCoachDismissed()) return;
    const coaches = document.querySelectorAll('[data-rfhp-flow-temp-coach]');
    if (!coaches.length) return;

    this._coachTimer = window.setTimeout(() => {
      this._coachTimer = null;
      coaches.forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        el.removeAttribute('hidden');
      });
      requestAnimationFrame(() => {
        coaches.forEach((el) => {
          if (!(el instanceof HTMLElement)) return;
          this._positionFlowCoach(el);
        });
        this._attachCoachReposition();
        requestAnimationFrame(() => {
          coaches.forEach((el) => {
            if (el instanceof HTMLElement) el.classList.add('rfhp-flow-temp__coach--visible');
          });
        });
      });
    }, 700);

    /** @type {Array<[HTMLElement, () => void]>} */
    const dismissHandlers = [];
    document.querySelectorAll('[data-rfhp-flow-temp-coach-dismiss]').forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      const fn = () => this._dismissFlowCoach();
      btn.addEventListener('click', fn);
      dismissHandlers.push([btn, fn]);
    });
    this._coachDismissBtnHandlers = dismissHandlers;
  },

  init() {
    if (this._initialized) return;
    this._initialized = true;
    this._current = this._loadFromStorage();
    this._bindSelect();
    this._bindFlowPanelMobile();
    this._syncSelectDom();
    this._variantCaptureListener = (e) => this._onVariantUpdateCapture(e);
    document.addEventListener(VARIANT_UPDATE, this._variantCaptureListener, true);
    this._outsidePointerListener = (e) => this._onPointerDownOutside(e);
    document.addEventListener('pointerdown', this._outsidePointerListener, true);
    this._observeDom();
    this._initFlowCoach();
    requestAnimationFrame(() => this.refreshAll());
  },
};

window.FlowTemperatureManager = FlowTemperatureManager;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => FlowTemperatureManager.init(), { once: true });
} else {
  FlowTemperatureManager.init();
}
